// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// Contacts directory.
//
// Lists everything imported via Tools → Import Contacts. Supports search
// (name/email/phone/company), source/tag filtering, pagination, sort,
// bulk delete (with DELETE confirmation), delete-all (same), CSV/vCard
// export of either the selected rows or everything, and click-through to
// the contact detail page.
//
// Contacts that have been converted into clients show a "→ Client" badge
// and link directly to the client's detail page.

import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const SOURCE_LABELS = {
  vcard_apple:   'Apple (vCard)',
  vcard_google:  'Google (vCard)',
  vcard_generic: 'vCard',
  google_csv:    'Google (CSV)',
  outlook_csv:   'Outlook (CSV)',
  manual:        'Manual',
};

const td = { padding: '8px 12px', fontSize: 13, fontFamily: 'Arial, sans-serif', borderBottom: '1px solid #eee', verticalAlign: 'top' };
const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, background: '#0F1838' };

export default function AdminContactsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven so the back button and refresh do the right thing.
  const q        = searchParams.get('q') || '';
  const source   = searchParams.get('source') || '';
  const page     = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const pageSize = Math.max(1, parseInt(searchParams.get('pageSize') || '25', 10));
  const sort     = searchParams.get('sort') || 'name_asc';

  const [data, setData]       = useState(null); // { total, page, pageSize, pages, rows }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [selected, setSelected] = useState(new Set());

  // Debounced search input — typing should not fire a request on every keystroke.
  const [qInput, setQInput] = useState(q);
  const qDebounce = useRef(null);
  function onSearchChange(v) {
    setQInput(v);
    if (qDebounce.current) clearTimeout(qDebounce.current);
    qDebounce.current = setTimeout(() => {
      // BUGFIX: previously we called setParam('q', v) and setParam('page', 1)
      // back-to-back. react-router v6's setSearchParams does NOT queue the
      // updater fn the way React's setState does — both updaters ran against
      // the same stale `prev`, the second one won, and the first (the actual
      // search!) got discarded. So typing in the box updated qInput but the
      // URL's `q` never changed, the data-fetch effect never re-ran, and the
      // search appeared to do nothing. We now apply both changes in a single
      // setSearchParams call so they're committed atomically.
      setParams({ q: v, page: 1 });
    }, 250);
  }

  // Set a single URL param. Treats '' / null / page=1 as "remove the key".
  function setParam(k, v) {
    setParams({ [k]: v });
  }

  // Set multiple URL params in one go. This is the only safe way to update
  // two or more params at once — see the comment in onSearchChange above.
  function setParams(updates) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === '' || v == null || (k === 'page' && Number(v) === 1)) {
          next.delete(k);
        } else {
          next.set(k, String(v));
        }
      }
      return next;
    }, { replace: true });
  }

  // If the URL's `q` changes from somewhere other than the search input
  // (back button, manual URL edit, programmatic navigation), keep the
  // visible input in sync.
  useEffect(() => {
    setQInput(q);
  }, [q]);

  // Fetch list on any query change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    adminApi.listContacts({ q, source, page, pageSize, sort })
      .then(d => { if (!cancelled) { setData(d); setLoading(false); setSelected(new Set()); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [q, source, page, pageSize, sort]);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  // Selection helpers
  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const someSelected = !allSelected && rows.some(r => selected.has(r.id));
  function toggleRow(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAllOnPage(v) {
    setSelected(prev => {
      const next = new Set(prev);
      for (const r of rows) v ? next.add(r.id) : next.delete(r.id);
      return next;
    });
  }

  // ── Bulk actions ─────────────────────────────────────────────────────────
  async function handleBulkDelete() {
    if (!selected.size) return;
    const word = window.prompt(
      `You're about to permanently delete ${selected.size} contact${selected.size === 1 ? '' : 's'}.\n\nType DELETE to confirm.`
    );
    if (word !== 'DELETE') return;
    try {
      await adminApi.bulkDeleteContacts(Array.from(selected), word);
      // Reload list — re-set page=1 if the current page is now empty.
      setSelected(new Set());
      refetch();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  async function handleDeleteAll() {
    const word = window.prompt(
      `You're about to permanently delete ALL contacts (${total} total).\n\nType DELETE to confirm.`
    );
    if (word !== 'DELETE') return;
    try {
      await adminApi.deleteAllContacts(word);
      setSelected(new Set());
      refetch();
    } catch (err) {
      alert('Delete all failed: ' + err.message);
    }
  }

  async function handleExport(format) {
    const ids = selected.size ? Array.from(selected) : null;
    try {
      const res = await adminApi.exportContacts({ ids, format });
      downloadBlob(res.content, res.filename, res.mime);
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
  }

  function refetch() {
    // Reload by bumping a no-op param. Cleanest is to re-set page; if we're
    // already on page 1, set the same value (the effect dep array is by
    // value, but searchParams identity changes either way).
    setSearchParams(prev => new URLSearchParams(prev), { replace: true });
    // Force the effect to re-run by toggling state in case the URL
    // didn't actually change. The simplest reliable approach: just call
    // the API directly here.
    setLoading(true);
    adminApi.listContacts({ q, source, page, pageSize, sort })
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }

  // ── Source dropdown options come from what's actually present in data. ─
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 4 }}>Contacts</h1>
          <p className="page-subtitle" style={{ marginTop: 0 }}>
            Personal and professional contacts imported from Google or Apple.
            Separate from clients.
          </p>
        </div>
        <Link to="/admin/dashboard/import-contacts" className="btn btn-primary">+ Import More</Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: '2 1 240px', minWidth: 220, marginBottom: 0 }}>
            <label>Search</label>
            <input
              type="text"
              value={qInput}
              onChange={e => onSearchChange(e.target.value)}
              placeholder="Name, email, phone, or company"
            />
          </div>
          <div className="form-group" style={{ flex: '1 1 160px', minWidth: 140, marginBottom: 0 }}>
            <label>Source</label>
            <select value={source} onChange={e => setParams({ source: e.target.value, page: 1 })}>
              <option value="">All sources</option>
              {Object.keys(SOURCE_LABELS).map(s => (
                <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 160px', minWidth: 140, marginBottom: 0 }}>
            <label>Sort</label>
            <select value={sort} onChange={e => setParam('sort', e.target.value)}>
              <option value="name_asc">Name (A→Z)</option>
              <option value="name_desc">Name (Z→A)</option>
              <option value="imported_desc">Newest imported</option>
              <option value="imported_asc">Oldest imported</option>
            </select>
          </div>
          <div className="form-group" style={{ flex: '0 0 100px', minWidth: 80, marginBottom: 0 }}>
            <label>Per page</label>
            <select value={pageSize} onChange={e => setParams({ pageSize: e.target.value, page: 1 })}>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Bulk-action toolbar ────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 12, padding: '10px 14px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: '#555', fontFamily: 'Arial, sans-serif' }}>
            {selected.size > 0
              ? <><strong>{selected.size}</strong> selected of {total.toLocaleString()}</>
              : <><strong>{total.toLocaleString()}</strong> total contact{total === 1 ? '' : 's'}</>}
          </div>
          <div className="btn-row" style={{ marginTop: 0 }}>
            {selected.size > 0 && (
              <>
                <button className="btn btn-sm" onClick={() => handleExport('csv')}>Export selected → CSV</button>
                <button className="btn btn-sm" onClick={() => handleExport('vcard')}>Export selected → vCard</button>
                <button className="btn btn-sm" style={{ color: '#B85042' }} onClick={handleBulkDelete}>
                  Delete selected
                </button>
              </>
            )}
            {selected.size === 0 && total > 0 && (
              <>
                <button className="btn btn-sm" onClick={() => handleExport('csv')}>Export all → CSV</button>
                <button className="btn btn-sm" onClick={() => handleExport('vcard')}>Export all → vCard</button>
                <button className="btn btn-sm" style={{ color: '#B85042' }} onClick={handleDeleteAll}>Delete ALL</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Results table ──────────────────────────────────────────────── */}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif' }}>
            Loading...
          </div>
        ) : !rows.length ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif' }}>
            {total === 0 && !q && !source
              ? <>No contacts yet. <Link to="/admin/dashboard/import-contacts">Import some →</Link></>
              : <>No contacts match your filters.</>}
          </div>
        ) : (
          // Scroll wrapper inside the card. Without this, a wide row (long
          // email, long company name) would either overflow the card or be
          // clipped — depending on whether the parent applied overflow.
          <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected; }}
                      onChange={e => toggleAllOnPage(e.target.checked)}
                    />
                  </th>
                  <th style={th}>Name</th>
                  <th style={th}>Company</th>
                  <th style={th}>Email</th>
                  <th style={th}>Phone</th>
                  <th style={th}>Source</th>
                  <th style={th}>Imported</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const primaryEmail = r.emails?.[0]?.value || '';
                  const primaryPhone = r.phones?.[0]?.value || '';
                  const converted = !!r.converted_to_client_id;
                  return (
                    <tr
                      key={r.id}
                      style={{ background: selected.has(r.id) ? '#f5f9ff' : 'white', cursor: 'pointer' }}
                      onClick={(e) => {
                        // Don't trigger navigation if the user clicked the checkbox.
                        if (e.target.type === 'checkbox') return;
                        navigate(`/admin/dashboard/contacts/${r.id}`);
                      }}
                    >
                      <td style={td} onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(r.id)}
                          onChange={() => toggleRow(r.id)}
                        />
                      </td>
                      <td style={td}>
                        <strong>{r.display_name}</strong>
                        {converted && (
                          <div style={{ marginTop: 2 }}>
                            <span style={{
                              display: 'inline-block', padding: '1px 6px', fontSize: 10,
                              background: '#e8f0fb', color: '#2f5496', borderRadius: 8, fontWeight: 600,
                            }}>→ Client</span>
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, wordBreak: 'break-word' }}>{r.company || ''}</td>
                      <td style={{ ...td, wordBreak: 'break-word' }}>{primaryEmail}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{primaryPhone}</td>
                      <td style={td}>
                        <span style={{ fontSize: 11, color: '#666' }}>
                          {SOURCE_LABELS[r.source] || r.source}
                        </span>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 11, color: '#888' }}>
                          {r.imported_at ? new Date(r.imported_at).toLocaleDateString() : ''}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────── */}
      {pages > 1 && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, color: '#666', fontFamily: 'Arial, sans-serif' }}>
            Page {page} of {pages}
          </div>
          <div className="btn-row" style={{ marginTop: 0 }}>
            <button className="btn btn-sm" onClick={() => setParam('page', 1)} disabled={page <= 1}>« First</button>
            <button className="btn btn-sm" onClick={() => setParam('page', page - 1)} disabled={page <= 1}>← Prev</button>
            <button className="btn btn-sm" onClick={() => setParam('page', page + 1)} disabled={page >= pages}>Next →</button>
            <button className="btn btn-sm" onClick={() => setParam('page', pages)} disabled={page >= pages}>Last »</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Trigger a browser download for an in-memory string blob.
function downloadBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
